import flatMap from "lodash/flatMap";
import uniqBy from "lodash/uniqBy";
import get from "lodash/get";
import currencyRanges from "./currencyRanges.json";
import { allDestinationItems, syncURLParams } from "./searchHelpers";

export const applyFilterURL = (state) => {
  const searchParams = new URLSearchParams(window.location.search);
  if (state.filters.budgets) {
    const currentBudgets = state.filters.budgets.split(",");
    const rangeInSelectedCurrency =
      currencyRanges[state.currency.code].liveaboard.filterRange;

    const results = [];
    currentBudgets.forEach((_b) => {
      results.push(rangeInSelectedCurrency[_b][0]);
    });

    searchParams.set("budgets", results.join(","));
  } else {
    searchParams.delete("budgets");
  }

  if (state.filters.availability) {
    searchParams.set("availability", state.filters.availability);
  } else {
    searchParams.delete("availability");
  }

  if (state.filters.destinations) {
    searchParams.set("destinations", state.filters.destinations);
  } else {
    searchParams.delete("destinations");
  }

  if (state.filters.rating) {
    searchParams.set("rating", state.filters.rating);
  } else {
    searchParams.delete("rating");
  }

  if (state.filters.tripLengths) {
    searchParams.set("tripLengths", state.filters.tripLengths);
  } else {
    searchParams.delete("tripLengths");
  }

  if (state.filters.facilities) {
    searchParams.set("facilities", state.filters.facilities);
  } else {
    searchParams.delete("facilities");
  }

  if (state.filters.species) {
    searchParams.set("species", state.filters.species);
  } else {
    searchParams.delete("species");
  }

  if (state.filters.numDives) {
    searchParams.set("numDives", state.filters.numDives);
  } else {
    searchParams.delete("numDives");
  }

  if (state.filters.tags) {
    searchParams.set("goodFor", state.filters.tags);
  } else {
    searchParams.delete("goodFor");
  }

  syncURLParams(searchParams);
};

export const getObjectIDFromName = (name) => {
  const entry = allDestinationItems.find((item) => item.name === name);
  if (entry) {
    return entry.id;
  }

  return null;
};

export const getPrimaryDestinationFilters = () => {
  if (window.zubluDestination.type === "country") {
    return `__platform_country_id:${window.zubluDestination.id}`;
  } else if (window.zubluDestination.type === "province") {
    return window.zubluDestination.children.map((dest) => {
      return `__odyssey_destination_full.code:${dest.code}`;
    });
  }

  return `__odyssey_destination_full.code:${window.zubluDestination.code}`;
};

export const getSecondaryDestinationFilters = (filters) => {
  if (filters.destinations) {
    const selectedIds = filters.destinations.split(",");
    const itemsSelected = [];

    if (window.zubluDestination.type === "province") {
      for (const destination of window.zubluDestination.children) {
        if (selectedIds.includes(destination.id.toString())) {
          itemsSelected.push(destination);
        }
      }
    } else if (window.zubluDestination.type === "country") {
      for (const province of window.zubluDestination.children) {
        if (selectedIds.includes(province.id.toString())) {
          itemsSelected.push(province);
        }

        for (const destination of province.children) {
          if (selectedIds.includes(destination.id.toString())) {
            itemsSelected.push(destination);
          }
        }
      }
    }

    return itemsSelected.map(
      (item) => `__odyssey_destination_full.code:${item.code}`
    );
  }

  return null;
};

export const getRatingFilters = (filters) => {
  const filteringRating = filters.rating.split(",").filter((x) => x);

  if (filteringRating.length === 0) {
    return null;
  }

  let list = [];
  if (filteringRating.includes("excellent")) {
    list.push("__platform_rating:5");
  }
  if (filteringRating.includes("very_good")) {
    list.push("__platform_rating:4");
  }
  if (filteringRating.includes("average")) {
    list.push("__platform_rating:3");
  }
  if (filteringRating.includes("poor")) {
    list.push("__platform_rating:1 TO 2");
  }
  if (filteringRating.includes("unrated")) {
    list.push("__platform_rating:0");
  }

  return list;
};

export const getLiveaboardsOption = (algoliaHit) => {
  return {
    type: "liveaboard",
    id: algoliaHit.objectID,
    title: algoliaHit.__platform_name,
    subtitle: `, liveaboard in ${algoliaHit.__platform_country_title}`,
    matchedCount: 100,
    weight: 1,
    url: algoliaHit.__platform_url,
  };
};

export const secondaryFilteredResults = ({
  suppliers,
  filters,
  destinationsForSelectedSpecies,
  experienceList,
  limitedDestinationCodes,
  currency,
}) => {
  const facilities = filters.facilities.split(",").filter((x) => x);

  const species = filters.species.split(",").filter((x) => x);

  const tripLengths = filters.tripLengths.split(",").filter((x) => x);

  const budgets = filters.budgets.split(",").filter((x) => x);

  const numDives = filters.numDives.split(",").filter((x) => x);

  const tags = filters.tags.split(",").filter((x) => x);

  const availability = filters.availability.split(",").filter((x) => x);

  const filteredSuppliers = suppliers
    .filter((supplier) => {
      if (facilities.length === 0) {
        return true;
      }

      return facilities.every((item) => {
        if (supplier.inclusions_objects) {
          return supplier.inclusions_objects.some((obj) => obj.slug === item);
        }
        return false;
      });
    })
    .filter((supplier) => {
      if (budgets.length === 0) {
        return true;
      }

      const rate = window.zubluCurrencyTable[currency.code];
      const ranges = budgets.map((option) => {
        const minValue = Number(option);
        const budgetByCurrency =
          currencyRanges[currency.code].liveaboard.filterRange[minValue];

        return [budgetByCurrency[0] * rate, budgetByCurrency[1] * rate];
      });

      return ranges.some((range) => {
        for (const trip of supplier.__odyssey_trips) {
          const price = get(trip, ["price", "price"], 0);

          if (price >= range[0] && price < range[1]) {
            return true;
          }
        }

        return false;
      });
    })
    .filter((supplier) => {
      if (experienceList.length === 0) {
        return true;
      }

      if (species.length === 0) {
        return true;
      }

      let destinationsInTrips = flatMap(
        supplier.__odyssey_trips,
        (trip) => trip.destinations
      );
      destinationsInTrips = uniqBy(destinationsInTrips, (dest) => dest.code);
      destinationsInTrips = destinationsInTrips.map((d) => d.code);

      return destinationsForSelectedSpecies.some((dest) => {
        return destinationsInTrips.includes(dest.__platform_skuKey);
      });
    })
    .filter((supplier) => {
      supplier.__odyssey_trips = supplier.__odyssey_trips.filter((trip) => {
        return (trip.destinations || []).some((destination) => {
          return limitedDestinationCodes.includes(destination.code);
        });
      });

      return supplier.__odyssey_trips.length;
    })
    .filter((supplier) => {
      if (numDives.length === 0) {
        return true;
      }

      if (numDives.includes("5")) {
        if (
          supplier.__odyssey_trips.some((trip) => {
            return [1, 2].includes(trip.experience_id);
          })
        ) {
          return true;
        }
      }
      if (numDives.includes("31")) {
        if (
          supplier.__odyssey_trips.some((trip) => {
            return [1, 2, 3, 4].includes(trip.experience_id);
          })
        ) {
          return true;
        }
      }
      if (numDives.includes("51")) {
        if (
          supplier.__odyssey_trips.some((trip) => {
            return [1, 2, 3, 4, 6, 7].includes(trip.experience_id);
          })
        ) {
          return true;
        }
      }
      if (numDives.includes("101")) {
        if (
          supplier.__odyssey_trips.some((trip) => {
            return [1, 2, 3, 4, 5, 6, 7].includes(trip.experience_id);
          })
        ) {
          return true;
        }
      }

      return false;
    })
    .filter((supplier) => {
      if (tripLengths.length === 0) {
        return true;
      }

      return supplier.__odyssey_trips.some((trip) => {
        if (tripLengths.includes("0")) {
          if (trip.length < 5) {
            return true;
          }
        }

        if (tripLengths.includes("5")) {
          if (trip.length >= 5 && trip.length < 8) {
            return true;
          }
        }

        if (tripLengths.includes("8")) {
          if (trip.length >= 8) {
            return true;
          }
        }
      });
    })
    .filter((supplier) => {
      const selectedMonth = moment(
        get(window, ["topNavStore", "state", "formData", "dates", "0"], null)
      );

      if (selectedMonth.isValid() === false) {
        return true;
      }

      return supplier.__odyssey_trips.some((trip) => {
        const mmTripDate = moment(trip.date, "YYYY-MM-DD");

        return (
          mmTripDate.month() === selectedMonth.month() &&
          mmTripDate.year() === selectedMonth.year()
        );
      });
    })
    .filter((supplier) => {
      if (tags.length === 0) {
        return true;
      }

      if (
        !supplier.__platform_search_tags ||
        !supplier.__platform_search_tags.length
      ) {
        return false;
      } else {
        return tags.every((tag) => {
          return supplier.__platform_search_tags.includes(tag);
        });
      }
    })
    .filter((supplier) => {
      if (availability.length === 0) {
        return true;
      }

      let available = availability.includes("available-trips");
      let offers = availability.includes("special-offers");

      for (const trip of supplier.__odyssey_trips) {
        if (
          available &&
          offers &&
          (trip.availability !== "Sold out" || !!trip.discount_tag)
        ) {
          return true;
        } else if (available && trip.availability !== "Sold out") {
          return true;
        } else if (offers && !!trip.discount_tag) {
          return true;
        }
      }

      return false;
    });

  return filteredSuppliers;
};

export const groupItems = (array, property) => {
  return array.reduce(function (groups, item) {
    var name = item[property];
    var group = groups[name] || (groups[name] = []);
    group.push(item);
    return groups;
  }, {});
};
